import type { ReviewItemView } from '@didto/domain';
import { EmptyState, Loading, Notice, PageHeader, SectionHead } from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useOwnerReview } from '@/app/ownerReview';
import { fetchReviewQueue, reviewKeys } from '@/lib/review';
import { ReadError, ReviewItemCard, useFormats } from './reviewItems';

/** Everything red of the last 31 days, one item at a time: OK with a reason, follow up, or resolve. */
export function ReviewQueuePage() {
  const { locationId } = useOwnerReview();
  const f = useFormats();
  const q = useQuery({
    queryKey: reviewKeys.queue(locationId),
    queryFn: () => fetchReviewQueue(locationId),
    refetchInterval: 60_000,
  });
  const byDate = useMemo(() => {
    const m = new Map<string, ReviewItemView[]>();
    for (const i of q.data?.items ?? [])
      m.set(i.business_date, [...(m.get(i.business_date) ?? []), i]);
    return [...m.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [q.data]);
  return (
    <div className="d-page" data-testid="review-queue">
      <PageHeader
        eyebrow="Owner"
        title="To check"
        subtitle="Each item is answered as it is shown; if its facts change, it comes back."
        actions={
          <Link to="/shifts" className="d-btn d-btn--sm">
            Shifts
          </Link>
        }
      />
      <ReadError error={q.error} hasData={!!q.data} onRetry={() => void q.refetch()} />
      {q.isLoading && <Loading label="Loading what is to check" rows={3} />}
      {q.data && q.data.items.length === 0 && (
        <EmptyState title="Nothing to check." testId="queue-empty" />
      )}
      {byDate.map(([date, items]) => (
        <section key={date} className="rv-section" aria-label={f.date(date)}>
          <SectionHead title={f.date(date)}>
            <Link to={`/shifts/day/${date}`} className="d-btn d-btn--sm d-btn--quiet">
              Day view
            </Link>
          </SectionHead>
          <div className="d-list">
            {items.map((i) => (
              <ReviewItemCard key={i.item_key} item={i} />
            ))}
          </div>
        </section>
      ))}
      {q.data && q.data.older_unreviewed_shifts > 0 && (
        <Notice tone="warning" testId="queue-older">
          {q.data.older_unreviewed_shifts} older shift
          {q.data.older_unreviewed_shifts === 1 ? '' : 's'} never marked reviewed, 32 to 90 days
          ago. Shifts before that are not counted here: find them on{' '}
          <Link to="/shifts">Shifts</Link> with To check and Earlier.
        </Notice>
      )}
    </div>
  );
}
